import React, { useState, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { Row, Col, Checkbox } from 'antd';
import { GlobalFormContact, GlobalFormInputName } from 'src/modules/global';
import { useLocale, OutputChannels } from 'src/modules/locale';
import styles from './GlobalFormBrochures.module.scss';
import { useIntl } from 'react-intl';
import { rowDefaultProps } from 'src/utils/grid';

import assets from '../../../../../../public/data/assets.json';

export interface GlobalFormBrochuresProps {
  title: string;
  fullWidth?: boolean;
  buttonText?: string;
  isOpen?: boolean;
  onClose?: () => void;
  additionalFormFields?: Record<string, string>;
}

let eventTarget: EventTarget;
const IMAGE_TOGGLE_EVENT = 'IMAGE_TOGGLE_EVENT';
const VALIDATION_FAILURE_EVENT = 'VALIDATION_FAILURE_EVENT';
const VALIDATION_SUCCESS_EVENT = 'VALIDATION_SUCCESS_EVENT';

const HOME_FORM_DATA = { id: '00N3X00000Lh88A' };
const CARPETS_FORM_DATA = { id: '00N3X00000Lh8Jr' };

const INPUT_ORDER: GlobalFormInputName[] = [
  'salutation',
  'empty',
  'firstName',
  'lastName',
  'company',
  'streetAndNumber',
  'zipCode',
  'city',
  'country',
  'email',
];

type ImageCardProps = {
  titleId: string;
  descriptionId: string;
  src: string;
  formData: { id: string };
};
const ImageCard = ({ titleId, descriptionId, src, formData }: ImageCardProps) => {
  const intl = useIntl();
  const [selected, setSelected] = useState(false);
  const toggleSelect = useCallback(() => {
    setSelected(!selected);
    eventTarget.dispatchEvent(
      new CustomEvent(IMAGE_TOGGLE_EVENT, { detail: { ...formData, selected: !selected } })
    );
  }, [selected, formData]);
  const [validationError, setValidationError] = useState(false);

  useEffect(() => {
    const onValidationSuccess = () => {
      setValidationError(false);
    };
    const onValidationFailure = () => {
      setValidationError(true);
    };

    if (!eventTarget) eventTarget = new EventTarget();
    eventTarget.addEventListener(VALIDATION_SUCCESS_EVENT, onValidationSuccess);
    eventTarget.addEventListener(VALIDATION_FAILURE_EVENT, onValidationFailure);

    return () => {
      eventTarget.removeEventListener(VALIDATION_SUCCESS_EVENT, onValidationSuccess);
      eventTarget.removeEventListener(VALIDATION_FAILURE_EVENT, onValidationFailure);
    };
  }, []);

  return (
    <Col md={12} xs={12} key={validationError ? 'brochure-form' : 'brochure-form-force-rerender'}>
      <div onClick={toggleSelect} className={styles.imageCardContainer}>
        <img className={styles.imageSelected} src={src} width={323} height={431} />
        <div className={styles.titleContainer}>
          <div className={styles.checkIcon}>
            <Checkbox className={validationError ? styles.errorCheckbox : ''} checked={selected} />
          </div>
          <h4 className={validationError ? styles.imageTitleError : styles.imageTitle}>
            {intl.formatMessage({ id: titleId })}
          </h4>
        </div>
        <p className={styles.description}>{intl.formatMessage({ id: descriptionId })}</p>
      </div>
    </Col>
  );
};

const BrochuresTopSection = () => {
  const { outputChannel } = useLocale();
  const intl = useIntl();

  const isUSA = outputChannel === OutputChannels.US;

  const imageLeft = isUSA
    ? assets?.Brochures_Form_Image_Home_WalterK?.[0]?.file?.url
    : assets?.Brochures_Form_Image_Home_WalterKnoll?.[0]?.file?.url;

  const imageRight = isUSA
    ? assets?.Brochures_Form_Image_Carpets_WalterK?.[0]?.file?.url
    : assets?.Brochures_Form_Image_Carpets_WalterKnoll?.[0]?.file?.url;

  return (
    <div className={styles.topSectionContainer}>
      <p className={styles.headline}>{intl.formatMessage({ id: 'form/brochures/headline' })}</p>
      <Row {...rowDefaultProps}>
        <ImageCard
          titleId="form/brochures/imageLeft/title"
          descriptionId="form/brochures/imageLeft/description"
          src={imageLeft}
          formData={HOME_FORM_DATA}
        />
        <ImageCard
          titleId="form/brochures/imageRight/title"
          descriptionId="form/brochures/imageRight/description"
          src={imageRight}
          formData={CARPETS_FORM_DATA}
        />
      </Row>
    </div>
  );
};

const GlobalFormBrochures: React.FunctionComponent<GlobalFormBrochuresProps> = ({
  title,
  buttonText,
  fullWidth = false,
}: GlobalFormBrochuresProps) => {
  const data = {
    oid: '00D3X000002KGzs',
    '00N3X00000Lx0Xn': 'Unknown',
    lead_source: 'Website',
    recordType: '0123X000001A3q3QAC',
    '00N3X00000LwRSM': 'Can you send me brochures?',
    retURL: (typeof window !== 'undefined' && window.location.href) || 'http://walterknoll.de',
  };

  const [additionalFormFields, setAdditionalFormFields] = useState({});

  useEffect(() => {
    const onImageToggle = (e: any) => {
      const { id, selected } = e?.detail;
      const newAdditionalFormFields = { ...additionalFormFields };

      if (additionalFormFields[id] && !selected) {
        delete newAdditionalFormFields[id];
      }

      if (!additionalFormFields[id] && selected) {
        newAdditionalFormFields[id] = 1;
        eventTarget.dispatchEvent(new Event(VALIDATION_SUCCESS_EVENT));
      }

      if (_.isEmpty(newAdditionalFormFields)) {
        eventTarget.dispatchEvent(new Event(VALIDATION_FAILURE_EVENT));
      }

      setAdditionalFormFields(newAdditionalFormFields);
    };

    if (!eventTarget) eventTarget = new EventTarget();
    eventTarget.addEventListener(IMAGE_TOGGLE_EVENT, onImageToggle);

    return () => {
      eventTarget.removeEventListener(IMAGE_TOGGLE_EVENT, onImageToggle);
    };
  }, [additionalFormFields]);

  return (
    <GlobalFormContact
      streetAndNumberRequired
      onlyShowZipcodeForGermany={false}
      customValidator={() => {
        const validatorSuccess = _.size(additionalFormFields) > 0;

        if (validatorSuccess) {
          eventTarget.dispatchEvent(new Event(VALIDATION_SUCCESS_EVENT));
        } else {
          eventTarget.dispatchEvent(new Event(VALIDATION_FAILURE_EVENT));
        }

        return validatorSuccess;
      }}
      data={data}
      url={process.env.NEXT_PUBLIC_SALESFORCE_FORM_URL}
      title={title}
      buttonText={buttonText}
      fullWidth={fullWidth}
      topJsx={<BrochuresTopSection />}
      additionalFormFields={additionalFormFields}
      inputsOrder={INPUT_ORDER}
      confirmationTitleId="form/brochures/confirmation/title"
      confirmationMessageId="form/brochures/confirmation/message"
    />
  );
};

export default GlobalFormBrochures;
